import React, { useState } from "react";
import api from "../api/axios";
import "./Login.responsive.css";

export default function Login({ onLogin, onSwitch }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleLogin = async () => {
    if (!email.trim() || !password.trim()) {
      alert("Please enter email and password");
      return;
    }

    try {
      setLoading(true);

      const res = await api.post("/api/auth/login", {
        email: email.trim(),
        password,
      });

      const data = res.data;

      localStorage.setItem("token", data.access_token);
      localStorage.setItem("user_name", data.name || "");
      localStorage.setItem("role", data.role || "");
      localStorage.setItem("user_id", data.user_id || "");

      onLogin({
        access_token: data.access_token,
        role: data.role,
        name: data.name || "",
        user_id: data.user_id || "",
      });
    } catch (err) {
      alert(err?.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = (e) => {
    e.preventDefault();
    handleLogin();
  };

  return (
    <div className="medgen-login-screen">
      <style>{loginCss}</style>

      <div className="medgen-login-bg-orb orb-one" />
      <div className="medgen-login-bg-orb orb-two" />

      <section className="medgen-login-shell">
        <aside className="medgen-login-intel">
          <div className="medgen-intel-top">
            <div className="medgen-logo-mark">
              <img src="/assets/favicon.png" alt="MedGen icon" />
            </div>

            <div>
              <span>MedGen Smart System</span>
              <h1>Medical Intelligence </h1>
            </div>
          </div>

          <p className="medgen-intel-copy">
            A secure clinical workspace for patient intake, family-linked
            profiles, structured case sheets, brain impact review, and research
            workflow support.
          </p>

          <div className="medgen-intel-grid">
            <div className="medgen-intel-card active">
              <span>01</span>
              <strong>Clinical Sessions</strong>
              <p>
                Continue structured patient review and smart case-sheet flow.
              </p>
            </div>

            <div className="medgen-intel-card">
              <span>02</span>
              <strong>Brain Impact Map</strong>
              <p>
                Review neuron-style impact areas and session-linked observations.
              </p>
            </div>

            <div className="medgen-intel-card">
              <span>03</span>
              <strong>Research Graph</strong>
              <p>
                Connect compounds, proteins, medicines, and clinical context.
              </p>
            </div>
          </div>

          <div className="medgen-pulse-panel">
            <div className="medgen-pulse-header">
              <span>Workspace Signal</span>
              <b>Live</b>
            </div>

            <div className="medgen-pulse-bars" aria-hidden="true">
              <i style={{ height: "42%" }} />
              <i style={{ height: "72%" }} />
              <i style={{ height: "54%" }} />
              <i style={{ height: "86%" }} />
              <i style={{ height: "63%" }} />
              <i style={{ height: "77%" }} />
            </div>
          </div>
        </aside>

        <main className="medgen-login-card-wrap">
          <div className="medgen-login-card">
            <div className="medgen-form-logo">
              <img src="/assets/logo.png" alt="MedGen" />
            </div>

            <div className="medgen-form-heading">
              <div className="medgen-eyebrow">Welcome back</div>
              <h2>Sign in to MedGen</h2>
              <p>
                Access your smart medical assistant workspace and continue
                clinical review securely.
              </p>
            </div>

            <form onSubmit={onSubmit} className="medgen-login-form">
              <div className="medgen-form-field">
                <label htmlFor="login-email">Email address</label>
                <div className="medgen-input-shell">
                  <span>@</span>
                  <input
                    id="login-email"
                    type="email"
                    placeholder="doctor@clinic.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoComplete="email"
                  />
                </div>
              </div>

              <div className="medgen-form-field">
                <label htmlFor="login-password">Password</label>
                <div className="medgen-input-shell">
                  <span>●</span>
                  <input
                    id="login-password"
                    type="password"
                    placeholder="Enter secure password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="current-password"
                  />
                </div>
              </div>

              <button
                className="medgen-login-btn"
                type="submit"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <i className="medgen-spinner" />
                    Signing in...
                  </>
                ) : (
                  <>Login to Workspace</>
                )}
              </button>

              <div className="medgen-auth-footer">
                <span>New to MedGen?</span>
                <button type="button" onClick={onSwitch}>
                  Create account →
                </button>
              </div>
            </form>

            <div className="medgen-secure-note">
              <span>Secure Workspace</span>
              <b>
                Clinical access is protected by authenticated session tokens.
              </b>
            </div>
          </div>
        </main>
      </section>
    </div>
  );
}

const loginCss = `
.medgen-login-screen {
  min-height: 100vh;
  position: relative;
  overflow: hidden;
  display: grid;
  place-items: center;
  padding: 34px;
  color: #07111f;
  background:
    radial-gradient(circle at 12% 4%, rgba(24, 183, 255, 0.18), transparent 28%),
    radial-gradient(circle at 92% 82%, rgba(31, 123, 193, 0.16), transparent 30%),
    linear-gradient(135deg, #f6fbff 0%, #edf6ff 45%, #ffffff 100%);
}

.medgen-login-screen * {
  box-sizing: border-box;
}

.medgen-login-bg-orb {
  position: absolute;
  border-radius: 999px;
  filter: blur(4px);
  pointer-events: none;
}

.medgen-login-bg-orb.orb-one {
  width: 420px;
  height: 420px;
  left: -160px;
  top: -160px;
  background: rgba(24, 183, 255, 0.14);
}

.medgen-login-bg-orb.orb-two {
  width: 360px;
  height: 360px;
  right: -140px;
  bottom: -160px;
  background: rgba(31, 123, 193, 0.13);
}

.medgen-login-shell {
  width: min(1180px, 100%);
  min-height: 690px;
  position: relative;
  z-index: 1;
  display: grid;
  grid-template-columns: minmax(420px, 1.05fr) minmax(390px, 0.95fr);
  border-radius: 34px;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.82);
  border: 1px solid rgba(185, 210, 235, 0.82);
  box-shadow: 0 34px 90px rgba(7, 17, 31, 0.14);
  backdrop-filter: blur(18px);
}

.medgen-login-intel {
  position: relative;
  isolation: isolate;
  padding: 42px;
  color: #ffffff;
  background:
    radial-gradient(circle at 82% 8%, rgba(24, 183, 255, 0.24), transparent 28%),
    radial-gradient(circle at 10% 90%, rgba(31, 123, 193, 0.22), transparent 34%),
    linear-gradient(145deg, #03060c 0%, #06101f 48%, #0b1b30 100%);
}

.medgen-login-intel::before {
  content: "";
  position: absolute;
  inset: 22px;
  z-index: -1;
  border-radius: 26px;
  border: 1px solid rgba(24, 183, 255, 0.12);
  pointer-events: none;
}

.medgen-intel-top {
  display: flex;
  align-items: center;
  gap: 16px;
}

.medgen-logo-mark {
  width: 58px;
  height: 58px;
  flex: 0 0 58px;
  display: grid;
  place-items: center;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(24, 183, 255, 0.18);
  box-shadow: 0 18px 42px rgba(24, 183, 255, 0.16);
}

.medgen-logo-mark img {
  width: 46px;
  height: 46px;
  object-fit: contain;
}

.medgen-intel-top span {
  display: block;
  margin-bottom: 5px;
  color: #9fdcff;
  font-size: 11px;
  font-weight: 900;
  letter-spacing: 0.22em;
  text-transform: uppercase;
}

.medgen-intel-top h1 {
  margin: 0;
  max-width: 440px;
  color: #ffffff;
  font-size: clamp(32px, 4vw, 48px);
  line-height: 1.02;
  letter-spacing: -0.055em;
  font-weight: 950;
}

.medgen-intel-copy {
  max-width: 560px;
  margin: 28px 0 28px;
  color: #a9bdd3;
  font-size: 15px;
  line-height: 1.75;
}

.medgen-intel-grid {
  display: grid;
  gap: 13px;
}

.medgen-intel-card {
  padding: 17px 18px;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.075);
  border: 1px solid rgba(255, 255, 255, 0.10);
}

.medgen-intel-card.active {
  background: rgba(255, 255, 255, 0.96);
  color: #07111f;
}

.medgen-intel-card span {
  display: block;
  margin-bottom: 8px;
  color: #65cfff;
  font-size: 11px;
  font-weight: 900;
  letter-spacing: 0.16em;
}

.medgen-intel-card.active span {
  color: #125a94;
}

.medgen-intel-card strong {
  display: block;
  margin-bottom: 4px;
  color: inherit;
  font-size: 15px;
  font-weight: 900;
}

.medgen-intel-card p {
  margin: 0;
  color: inherit;
  opacity: 0.72;
  font-size: 13px;
  line-height: 1.45;
}

.medgen-pulse-panel {
  margin-top: 24px;
  padding: 18px;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.075);
  border: 1px solid rgba(24, 183, 255, 0.13);
}

.medgen-pulse-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
}

.medgen-pulse-header span {
  color: #9fdcff;
  font-size: 11px;
  font-weight: 900;
  letter-spacing: 0.16em;
  text-transform: uppercase;
}

.medgen-pulse-header b {
  padding: 5px 10px;
  border-radius: 999px;
  color: #bfeaff;
  background: rgba(24, 183, 255, 0.12);
  border: 1px solid rgba(24, 183, 255, 0.18);
  font-size: 11px;
}

.medgen-pulse-bars {
  height: 105px;
  display: flex;
  align-items: end;
  gap: 12px;
  margin-top: 18px;
}

.medgen-pulse-bars i {
  flex: 1;
  min-height: 18px;
  border-radius: 999px 999px 10px 10px;
  background: linear-gradient(180deg, #18b7ff, #1f7bc1);
  box-shadow: 0 12px 24px rgba(24, 183, 255, 0.18);
}

.medgen-login-card-wrap {
  display: grid;
  place-items: center;
  padding: 42px;
}

.medgen-login-card {
  width: min(430px, 100%);
  padding: 34px;
  border-radius: 28px;
  background: rgba(255, 255, 255, 0.96);
  border: 1px solid rgba(185, 210, 235, 0.92);
  box-shadow: 0 24px 65px rgba(7, 17, 31, 0.10);
}

/* Dark capsule so the white Med text in logo.png is visible */
.medgen-form-logo {
  width: 100%;
  min-height: 88px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 24px;
  padding: 18px 22px;
  border-radius: 22px;
  background:
    radial-gradient(circle at 18% 0%, rgba(24, 183, 255, 0.22), transparent 34%),
    linear-gradient(135deg, #03060c 0%, #06101f 52%, #0b1b30 100%);
  border: 1px solid rgba(24, 183, 255, 0.18);
  box-shadow:
    inset 0 0 0 1px rgba(255, 255, 255, 0.035),
    0 18px 40px rgba(3, 6, 12, 0.16);
}

.medgen-form-logo img {
  display: block;
  width: auto;
  height: auto;
  max-width: 260px;
  max-height: 58px;
  object-fit: contain;
  object-position: center;
  filter: drop-shadow(0 0 12px rgba(24, 183, 255, 0.18));
}

.medgen-form-heading {
  text-align: center;
  margin-bottom: 24px;
}

.medgen-eyebrow {
  margin-bottom: 8px;
  color: #125a94;
  font-size: 11px;
  font-weight: 950;
  letter-spacing: 0.22em;
  text-transform: uppercase;
}

.medgen-form-heading h2 {
  margin: 0;
  color: #07111f;
  font-size: 32px;
  line-height: 1.08;
  letter-spacing: -0.055em;
  font-weight: 950;
}

.medgen-form-heading p {
  margin: 12px auto 0;
  max-width: 330px;
  color: #66778e;
  font-size: 14px;
  line-height: 1.65;
}

.medgen-login-form {
  display: grid;
  gap: 16px;
}

.medgen-form-field {
  display: grid;
  gap: 8px;
}

.medgen-form-field label {
  color: #30445d;
  font-size: 12px;
  font-weight: 900;
}

.medgen-input-shell {
  display: grid;
  grid-template-columns: 38px 1fr;
  align-items: center;
  min-height: 50px;
  border-radius: 14px;
  background: #f7fbff;
  border: 1px solid #cddcec;
  overflow: hidden;
  transition: 0.16s ease;
}

.medgen-input-shell:focus-within {
  background: #ffffff;
  border-color: #1f7bc1;
  box-shadow: 0 0 0 4px rgba(31, 123, 193, 0.12);
}

.medgen-input-shell span {
  display: grid;
  place-items: center;
  color: #1f7bc1;
  font-size: 13px;
  font-weight: 950;
}

.medgen-input-shell input {
  width: 100%;
  height: 100%;
  min-height: 50px;
  border: none;
  outline: none;
  background: transparent;
  color: #07111f;
  font-size: 14px;
  font-weight: 700;
}

.medgen-input-shell input::placeholder {
  color: #8aa0b8;
  font-weight: 600;
}

.medgen-login-btn {
  width: 100%;
  min-height: 52px;
  margin-top: 4px;
  border: none;
  border-radius: 14px;
  cursor: pointer;
  color: #ffffff;
  background: linear-gradient(135deg, #1f7bc1, #0f5f9d);
  box-shadow: 0 16px 34px rgba(31, 123, 193, 0.24);
  font-size: 14px;
  font-weight: 950;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  transition: 0.16s ease;
}

.medgen-login-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 20px 38px rgba(31, 123, 193, 0.28);
}

.medgen-login-btn:disabled {
  opacity: 0.72;
  cursor: not-allowed;
}

.medgen-spinner {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 2px solid rgba(255,255,255,0.38);
  border-top-color: #ffffff;
  animation: medgenSpin 0.8s linear infinite;
}

@keyframes medgenSpin {
  to {
    transform: rotate(360deg);
  }
}

.medgen-auth-footer {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 6px;
  margin-top: 6px;
  color: #66778e;
  font-size: 13px;
}

.medgen-auth-footer button {
  border: none;
  background: transparent;
  color: #125a94;
  cursor: pointer;
  font-weight: 950;
}

.medgen-secure-note {
  margin-top: 22px;
  padding: 14px;
  border-radius: 16px;
  background:
    radial-gradient(circle at 100% 0%, rgba(24, 183, 255, 0.12), transparent 30%),
    #f6faff;
  border: 1px solid #d8e7f5;
}

.medgen-secure-note span {
  display: block;
  margin-bottom: 5px;
  color: #125a94;
  font-size: 10px;
  font-weight: 950;
  letter-spacing: 0.16em;
  text-transform: uppercase;
}

.medgen-secure-note b {
  color: #30445d;
  font-size: 12px;
  line-height: 1.45;
}

@media (max-width: 980px) {
  .medgen-login-screen {
    padding: 18px;
  }

  .medgen-login-shell {
    grid-template-columns: 1fr;
    min-height: auto;
  }

  .medgen-login-intel {
    padding: 30px;
  }

  .medgen-login-card-wrap {
    padding: 26px;
  }

  .medgen-intel-top h1 {
    font-size: 34px;
  }
}

@media (max-width: 560px) {
  .medgen-login-screen {
    padding: 12px;
  }

  .medgen-login-shell {
    border-radius: 24px;
  }

  .medgen-login-intel {
    padding: 22px;
  }

  .medgen-login-card-wrap {
    padding: 18px;
  }

  .medgen-login-card {
    padding: 24px;
    border-radius: 22px;
  }

  .medgen-intel-top {
    align-items: flex-start;
  }

  .medgen-logo-mark {
    width: 48px;
    height: 48px;
    flex-basis: 48px;
  }

  .medgen-logo-mark img {
    width: 38px;
    height: 38px;
  }

  .medgen-intel-top h1 {
    font-size: 28px;
  }

  .medgen-form-logo {
    min-height: 76px;
    padding: 14px 18px;
  }

  .medgen-form-logo img {
    max-width: 210px;
    max-height: 50px;
  }

  .medgen-form-heading h2 {
    font-size: 28px;
  }

  .medgen-auth-footer {
    flex-direction: column;
    gap: 4px;
  }
}
`;